import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import axiosInstance from "../config/axios";

const formatTimeEST = (dateStr) => {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleString("en-CA", {
      timeZone: "America/Toronto",
      dateStyle: "short",
      timeStyle: "medium",
    });
  } catch {
    return String(dateStr);
  }
};

const formatDuration = (accessedAt, leftAt) => {
  if (!accessedAt) return "—";
  const start = new Date(accessedAt);
  const end = leftAt ? new Date(leftAt) : new Date();
  if (isNaN(start.getTime())) return "—";
  if (leftAt && isNaN(end.getTime())) return "—";
  const ms = end.getTime() - start.getTime();
  if (ms < 0) return "—";
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  if (hr > 0) return `${hr}h ${min % 60}m`;
  if (min > 0) return `${min}m ${sec % 60}s`;
  return `${sec}s`;
};

const isActive = (leftAt) => !leftAt;

export default function AccessLogPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadAccessLog();
  }, []);

  const loadAccessLog = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/access-log?limit=200");
      const logs = Array.isArray(res.data?.logs)
        ? res.data.logs
        : Array.isArray(res.data)
          ? res.data
          : [];
      setEntries(logs);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load access log.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-800">
              Who has accessed the system
            </h1>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Back to Dashboard
              </Link>
              <button
                type="button"
                onClick={loadAccessLog}
                className="px-4 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
              >
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <p className="text-gray-600">Loading…</p>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg shadow overflow-hidden">
              <div className="overflow-auto max-h-[calc(100vh-220px)]">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        IP Address
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Location
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Time (EST)
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {entries.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-12 text-center text-gray-500"
                        >
                          No access log entries yet.
                        </td>
                      </tr>
                    ) : (
                      entries.map((entry, i) => (
                        <tr key={entry._id || i} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {entry.userName || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {entry.userEmail}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {entry.ipAddress || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {entry.location || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatTimeEST(entry.accessedAt)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {isActive(entry.leftAt) ? (
                              <span title="Session ongoing">
                                {formatDuration(entry.accessedAt, null)}{" "}
                                (Active)
                              </span>
                            ) : (
                              formatDuration(entry.accessedAt, entry.leftAt)
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
